import React, { useEffect, useRef, useState } from "react";
import { Form, Button, Row, Col, ToggleButton, ToggleButtonGroup, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../DoctorForm/DoctorForm.css";
import CustomModal from "../../components/modal/Modal";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MapPin, Trash2 } from "lucide-react";
import { apiUrl } from "../../config/appConfig";
const NurseryForm = () => {
  const [fullName, setFullName] = useState("");
  const [onlineConsult, setOnlineConsult] = useState("No");
  const [phone, setPhone] = useState("");
  const [bio, setBio] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [yearsOfExperience, setYearOfExperience] = useState("");
  const [validated, setValidated] = useState(false);
  const [countryCode, setCountryCode] = useState("+964");
  const [hospitals, setHospitals] = useState([{ name: "", address: "", latitude: null, longitude: null }]);
  const [locationSuggestions, setLocationSuggestions] = useState([[]]);
  const sessionTokenRef = useRef(null);
  const dropdownRefs = useRef([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [region, setRegion] = useState("Baghdad");

  // Iraqi regions list
  const iraqiRegions = [
    "Baghdad",
    "Basra",
    "Al-Anbar",
    "Babil (Babylon)",
    "Dhi Qar",
    "Al-Qadisiyyah (Diwaniyah)",
    "Diyala",
    "Dohuk",
    "Erbil",
    "Sulaymaniyah",
    "Halabja",
    "Kirkuk",
    "Karbala",
    "Najaf",
    "Nineveh (Mosul)",
    "Salah Al-Din",
    "Wasit",
    "Maysan",
    "Al-Muthanna"
  ];

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      dropdownRefs.current.forEach((ref, index) => {
        if (ref && !ref.contains(event.target)) {
          setLocationSuggestions((prev) => {
            const next = [...prev];
            next[index] = [];
            return next;
          });
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch coordinates from placeId
  const fetchCoordinatesFromPlaceId = async (placeId) => {
    try {
      if (!window.google?.maps?.places) {
        return null;
      }

      const { Place } = await window.google.maps.importLibrary("places");
      
      const place = new Place({
        id: placeId,
      });

      await place.fetchFields({
        fields: ["location"],
      });

      if (place.location) {
        return {
          latitude: place.location.lat(),
          longitude: place.location.lng(),
        };
      }

      return null;
    } catch (error) {
      console.error("Error fetching coordinates:", error);
      return null;
    }
  };

  const fetchLocationSuggestions = async (index, value) => {
    if (!value.trim()) {
      setLocationSuggestions((prev) => {
        const next = [...prev];
        next[index] = [];
        return next;
      });
      return;
    }

    try {
      // Wait for Google Maps to be loaded
      if (!window.google?.maps?.places) {
        return;
      }

      // Create session token if not exists
      if (!sessionTokenRef.current && window.google?.maps?.places?.AutocompleteSessionToken) {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }

      // Use the new Places API with importLibrary
      const { AutocompleteSuggestion } = await window.google.maps.importLibrary("places");
      
      const request = {
        input: value,
        sessionToken: sessionTokenRef.current,
      };

      // Fetch suggestions using the new API
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      setLocationSuggestions((prev) => {
        const next = [...prev];
        next[index] = suggestions.map((suggestion) => {
          const placePrediction = suggestion.placePrediction;
          return {
            placeId: placePrediction.placeId,
            description: placePrediction.text.toString(),
          };
        });
        return next;
      });
    } catch (error) {
      console.error("Autocomplete suggestions error", error);
      setLocationSuggestions((prev) => {
        const next = [...prev];
        next[index] = [];
        return next;
      });
    }
  };

  const handleHospitalChange = (index, field, value, skipFetch = false) => {
    const updated = [...hospitals];
    updated[index][field] = value;
    setHospitals(updated);

    if (field === "address" && !skipFetch) {
      fetchLocationSuggestions(index, value);
    }
  };

  const handleAddHospital = () => {
    setHospitals([...hospitals, { name: "", address: "", latitude: null, longitude: null }]);
    setLocationSuggestions((prev) => [...prev, []]);
  };

  const handleRemoveHospital = (index) => {
    setHospitals(hospitals.filter((_, i) => i !== index));
    setLocationSuggestions((prev) => prev.filter((_, i) => i !== index));
  };

  const countryCodes = [
    { code: "+964", name: "Iraq" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        role_id: 7,
        full_name: fullName,
        hospital_details: hospitals
          .filter((h) => h.name.trim() !== "" && h.address.trim() !== "")
          .map((h) => ({ 
            name: h.name.trim(), 
            address: h.address.trim(),
            latitude: h.latitude,
            longitude: h.longitude
          })),
        online_consultation: onlineConsult === "Yes",
        contact_phone: `${countryCode}${phone}`,
        short_bio: bio,
        years_of_experience: parseInt(yearsOfExperience),
        region: region,
      };

      await axios.post(apiUrl('/professionals'), payload, {
        headers: { "Content-Type": "application/json" },
      });

      console.log('Form submitted successfully!');
      console.log('REACT_APP_FRONTEND_URL:', process.env.REACT_APP_FRONTEND_URL);
      setShowModal(true);
      setFullName("");
      setOnlineConsult("No");
      setPhone("");
      setBio("");
      setYearOfExperience("");
      setRegion("Baghdad");
      setValidated(false);
      setHospitals([{ name: "", address: "", latitude: null, longitude: null }]);
      setLocationSuggestions([[]]);
      toast.success("Your details have been saved successfully");
    } catch (error) {
      console.error("Error submitting data:", error);
      toast.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="main-container">
        <main className="main-content">
          <div className="content-wrapper">
            <div className="login-form-container">
              <h2 className="text-center">Nurse Details</h2>
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Form.Group className="form-group" controlId="fullName">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    className="form-input"
                    type="text"
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Full name is required
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="form-group" controlId="hospitalDetails">
                  <Form.Label>Hospital / Clinic Details</Form.Label>
                    {hospitals.map((h, index) => (
                    <div key={index} className="mb-3 border p-3 rounded" style={{ position: "relative" }}>
                      <Form.Control
                        className="mb-2"
                        type="text"
                        placeholder="Hospital/Clinic name"
                        value={h.name}
                        onChange={(e) => handleHospitalChange(index, "name", e.target.value)}
                        required
                      />
                      <div 
                        style={{ position: "relative" }}
                        ref={(el) => (dropdownRefs.current[index] = el)}
                      >
                        <div style={{ position: "relative" }}>
                          <MapPin 
                            size={18} 
                            style={{ 
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#6c757d",
                              zIndex: 1,
                              pointerEvents: "none"
                            }} 
                          />
                          <Form.Control
                            type="text"
                            placeholder="Search location..."
                            value={h.address}
                            onChange={(e) => handleHospitalChange(index, "address", e.target.value)}
                            required
                            autoComplete="off"
                            style={{
                              paddingLeft: "40px",
                              borderRadius: locationSuggestions[index]?.length > 0 ? "8px 8px 0 0" : "8px",
                              border: locationSuggestions[index]?.length > 0 ? "1px solid #0d6efd" : "1px solid #ced4da",
                              transition: "all 0.2s ease"
                            }}
                          />
                        </div>
                        {locationSuggestions[index]?.length > 0 && (
                          <div
                            style={{
                              position: "absolute",
                              zIndex: 1000,
                              width: "100%",
                              backgroundColor: "#fff",
                              border: "1px solid #dee2e6",
                              borderTop: "none",
                              borderRadius: "0 0 8px 8px",
                              maxHeight: 280,
                              overflowY: "auto",
                              boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
                            }}
                          >
                            {locationSuggestions[index].map((suggestion, idx) => (
                              <div
                                key={suggestion.placeId}
                                style={{
                                  padding: "12px 16px",
                                  cursor: "pointer",
                                  borderBottom: idx === locationSuggestions[index].length - 1 ? "none" : "1px solid #f0f0f0",
                                  background: "#fff",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "12px",
                                  transition: "all 0.2s ease",
                                }}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  
                                  // Update the input value without triggering fetch
                                  handleHospitalChange(index, "address", suggestion.description, true);
                                  
                                  // Fetch coordinates for the selected place
                                  const coords = await fetchCoordinatesFromPlaceId(suggestion.placeId);
                                  
                                  if (coords) {
                                    // Update hospital with coordinates
                                    setHospitals((prev) => {
                                      const updated = [...prev];
                                      updated[index] = {
                                        ...updated[index],
                                        latitude: coords.latitude,
                                        longitude: coords.longitude,
                                      };
                                      return updated;
                                    });
                                  }
                                  
                                  // Clear suggestions to close dropdown
                                  setLocationSuggestions((prev) => {
                                    const next = [...prev];
                                    next[index] = [];
                                    return next;
                                  });
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#f8f9fa";
                                  e.currentTarget.style.paddingLeft = "20px";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "#fff";
                                  e.currentTarget.style.paddingLeft = "16px";
                                }}
                              >
                                <MapPin 
                                  size={18} 
                                  style={{ 
                                    color: "#0d6efd", 
                                    marginTop: "2px",
                                    flexShrink: 0
                                  }} 
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ 
                                    fontSize: "14px", 
                                    color: "#212529",
                                    lineHeight: "1.5",
                                    fontWeight: "400"
                                  }}>
                                    {suggestion.description}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {hospitals.length > 1 && (
                        <Button 
                          variant="danger" 
                          size="sm" 
                          onClick={() => handleRemoveHospital(index)} 
                          className="mt-2"
                          title="Remove Hospital"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="secondary" onClick={handleAddHospital}>
                    Add Another Hospital
                  </Button>
                </Form.Group>

                <Form.Group className="form-group" controlId="onlineConsult">
                  <Form.Label>Are you interested in online consultation?</Form.Label>
                  <div style={{ marginTop: "0.5rem" }}>
                    <ToggleButtonGroup
                      type="radio"
                      name="onlineConsult"
                      value={onlineConsult}
                      onChange={(val) => setOnlineConsult(val)}
                    >
                      <ToggleButton
                        id="nurse-online-yes"
                        value="Yes"
                        variant={onlineConsult === "Yes" ? "primary" : "outline-primary"}
                        style={{ minWidth: 80 }}
                      >
                        Yes
                      </ToggleButton>
                      <ToggleButton
                        id="nurse-online-no"
                        value="No"
                        variant={onlineConsult === "No" ? "primary" : "outline-primary"}
                        style={{ minWidth: 80 }}
                      >
                        No
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </div>
                </Form.Group>

                <Form.Group className="form-group" controlId="experience">
                  <Form.Label>Years of Experience</Form.Label>
                  <Form.Control
                    className="form-input"
                    type="number"
                    placeholder="Enter Years of Experience"
                    value={yearsOfExperience}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || parseInt(val) >= 0) setYearOfExperience(val);
                    }}
                    required
                    min={0}
                  />
                  <Form.Control.Feedback type="invalid">
                    Years of experience must be 0 or greater
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="form-group form-group-check" controlId="contactPhone">
                  <Form.Label>Contact Phone</Form.Label>
                  <Row>
                    <Col xs={5}>
                      <Form.Select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} required style={{ fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {countryCodes.map((c, index) => (
                          <option key={`${c.code}-${c.name}-${index}`} value={c.code}>
                            {c.code} - {c.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col xs={7}>
                      <Form.Control
                        className="form-input"
                        type="tel"
                        placeholder="Enter phone"
                        value={phone}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (!/^[0-9]*$/.test(val)) return;
                          setPhone(val);
                        }}
                        required
                        pattern="^[0-9]{8,15}$"
                      />
                    </Col>
                  </Row>
                  <Form.Control.Feedback type="invalid">
                    Please enter a valid phone number (8–15 digits)
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Region Dropdown */}
                <Form.Group className="form-group" controlId="region">
                  <Form.Label>Region</Form.Label>
                  <div className="select-wrapper">
                    <Form.Select
                      className="form-select"
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      required
                    >
                      {iraqiRegions.map((regionName) => (
                        <option key={regionName} value={regionName}>
                          {regionName}
                        </option>
                      ))}
                    </Form.Select>
                    <span className="select-icon">&#9662;</span>
                  </div>
                </Form.Group>

                <Form.Group className="form-group" controlId="bio">
                  <Form.Label>Short Bio (optional)</Form.Label>
                  <Form.Control
                    className="form-input"
                    as="textarea"
                    rows={3}
                    placeholder="Tell us about your experience"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                  />
                </Form.Group>

                <Button variant="primary" type="submit" className="login-btn" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              </Form>
            </div>
          </div>
        </main>
      </div>
      <CustomModal 
        showModal={showModal} 
        onClose={() => setShowModal(false)} 
        autoRedirect={true}
        redirectUrl={process.env.REACT_APP_FRONTEND_URL}
      />
    </>
  );
};

export default NurseryForm;


